/**
 * The card face: PUBLIC-class stats only (alias, general traits, badges) per
 * A-2 — SENSITIVE_PHI / GENETIC content never renders here. The portrait slot
 * renders deterministic duotone monogram art derived from the id, unless a
 * short-lived signed photo URL was issued for a donor who opted in to
 * discovery-photo sharing (G-2 pattern) — that URL travels beside the A-2
 * card-face projection, never through it, and is never stored (E-7).
 *
 * Plain presentational component (no framer-motion) so the landing page can
 * render a real card preview without pulling in the deck bundle.
 * useTranslations resolves in both server and client renders.
 */
import { useTranslations } from "next-intl";

/** The card carries PUBLIC-class fields only (A-2) — see above. */
export interface SwipeCard {
  id: string;
  alias: string;
  stats: Record<string, string>;
  programBadge?: "FRESH" | "FROZEN" | "EMBRYO"; // donor cards only
  provenDonor?: boolean;
  /** Consent-gated signed URL (issueDiscoveryPhotoUrl) — expires in minutes. */
  photoUrl?: string;
}

/** Duotone pairs for placeholder art — warm, archival, deliberately un-photographic. */
const DUOTONES: Array<[string, string]> = [
  ["#5B6D92", "#DEE3EF"], // slate (brand accent since the 2026-08 rebrand)
  ["#B0713F", "#F2E2CC"], // ochre
  ["#6E6353", "#E9E1D2"], // umber
  ["#87684A", "#EFE0C8"], // tan
  ["#4E6E6A", "#D8E4DF"], // juniper
  ["#A4653E", "#F0DFD2"], // clay
];

function duotoneFor(id: string): [string, string] {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return DUOTONES[h % DUOTONES.length];
}

/** "Donor #9001 (SYNTHETIC)" → "9001"; "Family Charlie (SYNTHETIC)" → "FC". */
function monogramFor(alias: string): string {
  const num = alias.match(/#(\d+)/)?.[1];
  if (num) return num;
  const words = alias.replace(/\(.*?\)/g, "").trim().split(/\s+/);
  return words.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") || alias.slice(0, 2).toUpperCase();
}

export function CardFace({ card }: { card: SwipeCard }) {
  const t = useTranslations("cardFace");
  const tStats = useTranslations("stats");
  const [deep, pale] = duotoneFor(card.id);
  // "(SYNTHETIC)" = seed fixtures (H-5); "(SAMPLE)" = the public /preview
  // deck's fictional profiles. Both get an always-visible honesty ribbon.
  const ribbon = card.alias.includes("(SAMPLE)")
    ? t("sampleRibbon")
    : card.alias.includes("(SYNTHETIC)")
      ? t("syntheticRibbon")
      : null;
  return (
    <div className="card-face">
      <div
        className="card-portrait"
        style={{ background: `linear-gradient(158deg, ${pale} 0%, ${deep} 130%)` }}
        aria-hidden="true"
      >
        {card.photoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element -- short-lived signed URL; next/image would proxy+cache it (E-7)
          <img className="card-photo" src={card.photoUrl} alt="" />
        ) : (
          <span className="card-monogram" style={{ color: deep }}>
            {monogramFor(card.alias)}
          </span>
        )}
        {ribbon && <span className="card-synthetic-ribbon">{ribbon}</span>}
        <div className="card-badges">
          {card.programBadge && <span className="badge badge-program">{tStats(`program.${card.programBadge}`)}</span>}
          {card.provenDonor && <span className="badge badge-proven">{t("provenDonor")}</span>}
        </div>
      </div>
      <div className="card-body">
        <h2 className="card-alias">{card.alias.replace(/\s*\((SYNTHETIC|SAMPLE)\)\s*/, "")}</h2>
        <dl className="card-stats">
          {Object.entries(card.stats).map(([label, value]) => (
            <div className="card-stat" key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
